import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { citiesAPI } from '../utils/api';
import Seo from '../components/Seo';
import { Button } from '../components/ui/button';
import { MapPin, Navigation, Landmark as LandmarkIcon, Car, ArrowLeft } from 'lucide-react';

const CityDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [city, setCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    citiesAPI.getBySlug(slug)
      .then((res) => setCity(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500">Loading...</div>;
  }

  if (notFound || !city) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" data-testid="city-not-found">
        <p className="text-2xl font-semibold">City not found</p>
        <Button onClick={() => navigate('/cities')}>Back to Cities</Button>
      </div>
    );
  }

  const cityJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'TouristDestination',
    name: city.name,
    description: city.intro,
    image: city.profile_image,
    address: { '@type': 'PostalAddress', addressRegion: 'Gujarat', addressCountry: 'IN' },
  };

  return (
    <div className="min-h-screen bg-white" data-testid="city-detail-page">
      <Seo
        title={city.seo_title || `${city.name} | Ecoway Cabs`}
        description={city.seo_description || city.intro}
        keywords={city.seo_keywords}
        image={city.profile_image}
        path={`/cities/${city.slug}`}
        jsonLd={cityJsonLd}
      />

      {/* Hero */}
      <section className="relative h-[420px] flex items-end">
        <div className="absolute inset-0 bg-gray-800">
          {city.profile_image && (
            <img src={city.profile_image} alt={city.name} className="w-full h-full object-cover opacity-70" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 w-full">
          <Link to="/cities" className="inline-flex items-center gap-1 text-white/80 hover:text-white mb-4 text-sm" data-testid="back-to-cities">
            <ArrowLeft className="h-4 w-4" /> All Cities
          </Link>
          <div className="flex items-center gap-2 text-blue-200 mb-2">
            <MapPin className="h-5 w-5" />
            <span className="font-medium">Gujarat, India</span>
          </div>
          <h1 className="text-5xl font-bold text-white">{city.name}</h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-12">
          {city.intro && (
            <section>
              <h2 className="text-2xl font-bold mb-3">About {city.name}</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{city.intro}</p>
            </section>
          )}

          {city.landmarks && city.landmarks.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold mb-5 flex items-center gap-2"><LandmarkIcon className="h-6 w-6 text-blue-700" />Key Landmarks</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {city.landmarks.map((lm, i) => (
                  <div key={`${lm.name}-${i}`} className="border rounded-xl overflow-hidden" data-testid={`landmark-${i}`}>
                    {lm.image && <img src={lm.image} alt={lm.name} className="w-full h-36 object-cover" />}
                    <div className="p-4">
                      <h3 className="font-semibold mb-1">{lm.name}</h3>
                      {lm.description && <p className="text-sm text-gray-600">{lm.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {city.how_to_reach && (
            <section>
              <h2 className="text-2xl font-bold mb-3 flex items-center gap-2"><Navigation className="h-6 w-6 text-blue-700" />How to Get There from Ahmedabad</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{city.how_to_reach}</p>
            </section>
          )}

          {city.how_ecoway_helps && (
            <section className="bg-blue-50 rounded-2xl p-6">
              <h2 className="text-2xl font-bold mb-3 flex items-center gap-2"><Car className="h-6 w-6 text-blue-700" />How Ecoway Cabs Can Help</h2>
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">{city.how_ecoway_helps}</p>
              <Button className="mt-5" onClick={() => navigate('/quote')} data-testid="city-cta-quote">Get a Quote for {city.name}</Button>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-1">
          {city.key_details && city.key_details.length > 0 && (
            <div className="bg-white border rounded-2xl p-6 sticky top-24 shadow-sm">
              <h3 className="text-lg font-semibold mb-4">Key Details</h3>
              <dl className="space-y-3">
                {city.key_details.map((kd, i) => (
                  <div key={`${kd.label}-${i}`} className="flex justify-between gap-4 border-b last:border-0 pb-3 last:pb-0" data-testid={`key-detail-${i}`}>
                    <dt className="text-sm text-gray-500">{kd.label}</dt>
                    <dd className="text-sm font-medium text-right">{kd.value}</dd>
                  </div>
                ))}
              </dl>
              <Button className="w-full mt-5" onClick={() => navigate('/quote')}>Book a Cab</Button>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default CityDetailPage;
